import React, { useState, useEffect, useRef } from 'react'

interface Image {
   src?: string,
   srcMobile?: string,
   alt?: string,
   caption?: string,
}

interface Props {
   images?: Image[],
   contentColor?: "primary" | "secondary",
   size?: "large" | "medium" | "small",
   id?: string,
   isMacro?: boolean,
}

const arrowPrev = (
   <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.25 12H4.5" stroke="#002855" strokeWidth="2" strokeLinecap="square" strokeLinejoin="round" />
      <path d="M10.5 5.25L3.75 12L10.5 18.75" stroke="#002855" strokeWidth="2" strokeLinecap="square" />
   </svg>
)

const arrowNext = (
   <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.75 12H19.5" stroke="#002855" strokeWidth="2" strokeLinecap="square" strokeLinejoin="round" />
      <path d="M13.5 5.25L20.25 12L13.5 18.75" stroke="#002855" strokeWidth="2" strokeLinecap="square" />
   </svg>
)

export default function NewCarousel(props: Props) {
	const { images, contentColor, size, id, isMacro } = props;

	const carouselRef = useRef(null);
	const [hasRichTextParent, setHasRichTextParent] = useState<boolean>(null);
 
	const widthRef = useRef<HTMLDivElement>(null);
	const macroWidthRef = useRef<HTMLDivElement>(null);
	const [containerWidth, setContainerWidth] = useState<number>(0);
 
	const containerAlignmentStyles = {
		...(containerWidth ? { width: `${containerWidth}px` } : {}),
		marginInline: 'auto'
	}	
 
	const marginRef = useRef<HTMLDivElement>(null);
	const [containerMargin, setContainerMargin] = useState<number>(0);
 
	const [gap, setGap] = useState<number>(0);
	const desktopLarge = 1440;
	const desktopMedium = 1240;
	const largeGap = 100;
	const smallGap = 16;
	const correctionMultiplier = 1.52;
 
	const indexRef = useRef<HTMLDivElement>(null);
	const [currentSlide, setCurrentSlide] = useState<number>(0);
 
	const [progress, setProgress] = useState<number>(0);
 
	const [isFirst, setIsFirst] = useState<boolean>(true);
	const [isLast, setIsLast] = useState<boolean>(false);
 
	const [documentWidth, setDocumentWidth] = useState<number>(0);
	const [offset, setOffset] = useState<number>(0);
 
	// Ensure images are fully loaded before calculating anything
	const [imagesLoaded, setImagesLoaded] = useState<boolean>(false);
 
	const referenceSizeClass = () => {
		switch (size) {
			case 'large':
				return '';
			case 'medium':
				return 'c-md-10';
			case 'small':
				return 'c-md-8';
			default:
				return '';
		}
	}
	
	const handleButtonPrevious = () => {
		if (!isFirst) {
			indexRef.current?.scrollTo({
				left: (currentSlide - 1) * (containerWidth + gap),
				behavior: 'smooth'
			});
		}
	}
	
	const handleButtonNext = () => {
		if (!isLast) {
			indexRef.current?.scrollTo({
				left: (currentSlide + 1) * (containerWidth + gap),
				behavior: 'smooth'
			});
		}
	}
	
	useEffect(() => {
		const checkForRichTextParent = () => {
			let currentElement = carouselRef.current
	
			while (currentElement && currentElement !== document.body) {
				if (currentElement.classList.contains('rich-text')) {
					setHasRichTextParent(true)
					return
				}
	
				if (currentElement.tagName.toLowerCase() === 'main') {
					setHasRichTextParent(false)
					return
				}
	
				currentElement = currentElement.parentElement
			}
	
			setHasRichTextParent(false)
		}
	
		const calculateContainerWidth = () => {
			const computedWidth = window.getComputedStyle(widthRef.current).width
			setContainerWidth(parseInt(computedWidth, 10))
		}
	
		const calculateGap = () => {
			document.documentElement.clientWidth <= desktopLarge ? setGap(smallGap) : setGap(largeGap)
		}
	
		const calculateDocumentWidth = () => {
			setDocumentWidth(document.documentElement.clientWidth)
		}
	
		const calculateOffset = () => {
			const elementLeft = isMacro ? macroWidthRef?.current.getBoundingClientRect().left : widthRef?.current.getBoundingClientRect().left
			const documentLeft = window.scrollX
			const offset = documentLeft - elementLeft
			setOffset(offset)
		}
	
		const calculateIndex = () => {
			if (!images || images.length === 0 || !imagesLoaded) {
				setCurrentSlide(0)
				setProgress(0)
				setIsFirst(true)
				setIsLast(true)
				return
			}
	
			const currentScrollLeft = indexRef.current?.scrollLeft
			const scrollableWidth = indexRef.current?.scrollWidth - (2 * containerMargin)
			const itemWidth = scrollableWidth / images.length
			const index = Math.trunc(currentScrollLeft / itemWidth)
	
			setCurrentSlide(index)
			setProgress((100 / images.length) * (index + 1))
	
			index === 0 ? setIsFirst(true) : setIsFirst(false)
			index === images.length - 1 ? setIsLast(true) : setIsLast(false)
		}
	
		const handleResize = () => {
			calculateContainerWidth()
			setContainerMargin(parseInt(window.getComputedStyle(marginRef?.current).marginLeft, 10))
			calculateGap()
			calculateIndex()
			calculateOffset()
			calculateDocumentWidth()
		}
	
		checkForRichTextParent()
		calculateContainerWidth()
		setContainerMargin(parseInt(window.getComputedStyle(marginRef?.current).marginLeft, 10))
		calculateGap()
		calculateIndex()
		calculateOffset()
		calculateDocumentWidth()
	
		window.addEventListener('resize', handleResize)
		indexRef.current.addEventListener('scroll', calculateIndex)
	
		// ensure images are loaded
		if (images && images.length > 0) {
			setImagesLoaded(true)
		}
	
		return () => {
			window.removeEventListener('resize', handleResize);
			indexRef.current?.removeEventListener('scroll', calculateIndex);
		}
	}, [containerWidth, currentSlide, containerMargin, imagesLoaded]);

	useEffect(() => {
		if (imagesLoaded && indexRef.current) {
			indexRef.current.scrollTo({ left: 0 })
		}
	}, [imagesLoaded])

	return (
		<>
			<div ref={macroWidthRef} style={{
				position: 'absolute',
				left: '-9999px',
				width: '100vw',
				zIndex: '-99',
				opacity: '0',
				visibility: 'hidden',
				clipPath: 'inset(50%)'
			}}>
				<div className='grid-container container-reference'>
					<div className='grid-row'>
						<div
							ref={widthRef}
							className={`c-12 ${referenceSizeClass()}`}
						></div>
					</div>
				</div>
			</div>

			<div
				ref={carouselRef}
				className={`
					carousel-new
					carousel--${contentColor === 'primary' ? 'primary' : 'secondary'}
					${!documentWidth && !offset ? 'carousel-new--preview-mode' : ''}
				`}
				id={id || ''}
				style={{
					...(isMacro ? {
						...(documentWidth ? { width: `${documentWidth}px` } : {}),
						...(offset ? { marginLeft: `calc(${offset}px - 9999px)` } : {})
					} : {}),					
					//width: `${isMacro ? documentWidth : ''}${isMacro ? 'px' : ''}`,
					//marginLeft: `calc(${isMacro ? offset : ''}${isMacro ? 'px' : ''} - 9999px)`,
					position: 'relative',
					overflow: 'hidden',
				}}
			>
				<div
					ref={indexRef}
					className="carousel-new__track"
					role='list'
					style={{ paddingInline: `${containerMargin}px`, gap: `${gap}px` }}
				>
					{images && images.map((image, index) => (
						<div
							className={`
								carousel-new__item
								${index === currentSlide ? 'carousel-new__item--current' : ''}
								${index === currentSlide - 1 ? 'carousel-new__item--prev' : ''}
								${index === currentSlide + 1 ? 'carousel-new__item--next' : ''}
							`}
							key={index}
							role='listitem'
							style={containerAlignmentStyles}
						>
							<picture>
								<source media="(max-width: 768px)" srcSet={image.srcMobile || image?.src} />
								<img
									draggable={false}
									src={image?.src && image.src}
									alt={image?.alt && image.alt}
								/>
							</picture>
						</div>
					))}
				</div>

				<div ref={marginRef} className="carousel-new__content-wrapper" style={containerAlignmentStyles}>
					<div className="carousel-new__info">
						<div className="carousel-new__index">{currentSlide + 1}</div>
						<div className="carousel-new__caption">
							{images && (
								<div
									dangerouslySetInnerHTML={{ __html: images[currentSlide]?.caption }}
									suppressHydrationWarning
								></div>
							)}
						</div>
					</div>

					{images?.length !== 0 && (
						<div className="carousel-new__arrows">

							<button
								onClick={handleButtonPrevious}
								className="carousel-new__arrow carousel-new__arrow--prev"
								disabled={isFirst}
								aria-label='Previous'
							>
								{arrowPrev}
							</button>

							<button
								onClick={handleButtonNext}
								className="carousel-new__arrow carousel-new__arrow--next"
								disabled={isLast}
								aria-label='Next'
							>
								{arrowNext}
							</button>

						</div>
					)}

				</div>

				<div className="carousel-new__progress-indicator" style={containerAlignmentStyles}>
					<div className="indicator-base"></div>
					<div className="indicator-progress" style={{ width: `${progress || 0}%` }}></div>
				</div>

			</div>
		</>
	)
}